import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { longHourInterviewApi } from "../../services/longHourInterviewApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function LongHourInterviewDashboardPage() {
  const { t } = useLocalization();
  const { canViewLongHourInterview, canManageLongHourInterview } = usePermissions();
  const [policy, setPolicy] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [detecting, setDetecting] = useState(false);

  const load = () => {
    longHourInterviewApi.getPolicy().then((r) => setPolicy(r.data)).catch(() => {});
    longHourInterviewApi.listCandidates().then((r) => setCandidates(r.data)).catch(() => {});
  };
  useEffect(() => { if (canViewLongHourInterview) load(); }, [canViewLongHourInterview]);

  if (!canViewLongHourInterview) return <p className="text-sm text-gray-500" data-testid="long-hour-access-denied">{t("common.accessDenied")}</p>;

  const detect = async () => {
    setDetecting(true);
    try {
      const r = await longHourInterviewApi.detectCandidates();
      toast.success(`${t("mgmtLongHourInterview.detectionComplete")}: ${r.data.created} ${t("mgmtLongHourInterview.new")}, ${r.data.updated} ${t("mgmtLongHourInterview.alreadyTracked")} (${t("common.status")}: ${r.data.status})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDetecting(false); }
  };

  const openCount = candidates.filter((c) => !["CLOSED"].includes(c.status)).length;

  return (
    <div className="space-y-5" data-testid="long-hour-interview-dashboard-page">
      <PageHelpBanner pageKey="long-hour-interview-dashboard" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtLongHourInterview.dashboardHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtLongHourInterview.title")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtLongHourInterview.subtitle")}</p>
      </div>

      {policy && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between flex-wrap gap-2" data-testid="long-hour-policy-panel">
          <div>
            <h3 className="text-sm font-semibold text-gray-700">{t("mgmtLongHourInterview.policyEstablishmentStatus")}</h3>
            <p className="text-xs text-gray-500">{t("mgmtLongHourInterview.monitoringPeriod")}: {policy.monitoring_period_days} {t("mgmtLeaveDetail.days").toLowerCase()}</p>
          </div>
          <StatusBadge status={policy.establishment_status} />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/management/compliance/long-hour-interviews/policies" data-testid="long-hour-policies-card" className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <p className="text-2xl font-bold text-gray-900">→</p>
          <p className="text-xs text-gray-500">{t("mgmtLongHourInterview.policyThresholdRules")}</p>
        </Link>
        <Link to="/management/compliance/long-hour-interviews/candidates" data-testid="long-hour-candidates-card" className="bg-white rounded-xl border border-gray-200 p-5 hover:border-blue-300 transition-colors">
          <p className="text-2xl font-bold text-gray-900">{openCount}</p>
          <p className="text-xs text-gray-500">{t("mgmtLongHourInterview.openCandidates")} ({candidates.length} {t("mgmtLongHourInterview.total")})</p>
        </Link>
      </div>

      {canManageLongHourInterview && (
        <button data-testid="long-hour-detect-btn" disabled={detecting} onClick={detect} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
          {detecting ? t("mgmtLongHourInterview.detectingEllipsis") : t("mgmtLongHourInterview.runCandidateDetection")}
        </button>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-700">{t("mgmtLongHourInterview.recentCandidates")}</h3></div>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-2 font-medium">{t("mgmtLongHourInterview.colPeriod")}</th><th className="px-4 py-2 font-medium">{t("mgmtLongHourInterview.colHours")}</th><th className="px-4 py-2 font-medium">{t("common.status")}</th><th className="px-4 py-2"></th></tr>
          </thead>
          <tbody>
            {candidates.slice(0, 8).map((c) => (
              <tr key={c.id} className="border-t border-gray-100" data-testid={`long-hour-candidate-row-${c.id}`}>
                <td className="px-4 py-2 text-gray-700">{c.monitoring_period}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{c.approved_hours}h</td>
                <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-2"><Link to={`/management/compliance/long-hour-interviews/${c.id}`} className="text-xs font-semibold text-blue-600 hover:underline" data-testid={`long-hour-candidate-view-${c.id}`}>{t("mgmtLongHourInterview.view")}</Link></td>
              </tr>
            ))}
            {candidates.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-long-hour-candidates">{t("mgmtLongHourInterview.noCandidatesDetected")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
